import type { ListBuilderColumn } from '../../types';

/**
 * Shared column builders for the purpose-built compliance lists (PD-16252).
 * Every list has the same five-part row — Account | License | one list-specific
 * column | Why it's here | action — so the columns carry comparable weight
 * instead of one wide column beside several near-empty ones.
 */
const TABLE = 'client_license';
const plain = { size: 'sm' as const, colorRule: { type: 'default' as const } };
const muted = {
	size: 'xs' as const,
	colorRule: { type: 'theme' as const, themeColor: 'textSecondary' as const },
};
const lineBreak = (id: string): ListBuilderColumn['fields'][number] => ({
	id,
	dataConfig: { type: 'custom_text', customText: '' },
	displayStyle: 'line_break',
});

/** Account: name (links to the client), city/state, and 90-day sales. */
export const accountColumn: ListBuilderColumn = {
	id: 'compliance-account',
	fields: [
		{
			id: 'account-name',
			link: {
				type: 'custom',
				enabled: true,
				urlTemplate: '/clients/{clientId}',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientName', objectTable: TABLE },
			},
			styleConfig: {
				size: 'sm',
				weight: 'medium',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
			},
			displayStyle: 'text',
		},
		lineBreak('account-break-1'),
		{
			id: 'account-location',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientCityStateZip', objectTable: TABLE },
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'Account',
	headers: [{ id: 'h-account', text: 'Account' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [
		{ direction: 'ASC', fieldName: 'clientName', objectTable: TABLE },
	],
	minWidth: 190,
};

/** 90-day gross sales: the size of the account, and so of the risk. */
export const salesColumn: ListBuilderColumn = {
	id: 'compliance-sales-90',
	fields: [
		{
			id: 'sales-90-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientRevenue90', objectTable: TABLE },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				weight: 'semibold',
				colorRule: { type: 'default' },
				formatOptions: { numberFormat: 'currency', decimalPlaces: 0 },
			},
			displayStyle: 'currency',
		},
	],
	label: 'Sales 90d',
	headers: [{ id: 'h-sales-90', text: 'Sales 90d' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	alignment: 'right',
	sortable: true,
	sortFields: [
		{ direction: 'DESC', fieldName: 'clientRevenue90', objectTable: TABLE },
	],
	width: 100,
};

/** License: number (links to the license) over its type. */
export const licenseColumn: ListBuilderColumn = {
	id: 'compliance-license',
	fields: [
		{
			id: 'license-number',
			link: { type: 'custom', enabled: true, urlTemplate: '/licenses/{id}' },
			dataConfig: {
				type: 'field',
				// PD-16466: the server's display form of the number — a CMEA
				// self-certification has none and reads as a label.
				field: { fieldName: 'licenseNumberLabel', objectTable: TABLE },
			},
			styleConfig: {
				size: 'sm',
				weight: 'medium',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
			},
			displayStyle: 'text',
		},
		lineBreak('license-break-1'),
		{
			id: 'license-type',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'typeLabel', objectTable: TABLE },
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'License',
	headers: [{ id: 'h-license', text: 'License' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [
		{ direction: 'ASC', fieldName: 'licenseNumber', objectTable: TABLE },
	],
	width: 150,
};

/** Your file vs the regulator: "Active to 3/31/28" over "Cancelled 9/8/25". */
export const fileVsRegulatorColumn: ListBuilderColumn = {
	id: 'compliance-file-vs-regulator',
	fields: [
		{
			id: 'regulator-summary',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'regulatorSummary', objectTable: TABLE },
				defaultValue: 'No record',
			},
			styleConfig: {
				size: 'sm',
				weight: 'semibold',
				// Colored by the regulator's status, not always red: an in-force
				// license on the Expiring Soon or Link Ended list is not an alarm.
				colorRule: {
					type: 'conditional',
					rules: [
						{
							field: { fieldName: 'regulatorStatus', objectTable: TABLE },
							operator: 'equals',
							value: 'Active',
							color: { themeColor: 'statusSuccess' },
						},
						{
							field: { fieldName: 'regulatorStatus', objectTable: TABLE },
							operator: 'in',
							value: ['Pending', 'Probation', 'Limited'],
							color: { themeColor: 'statusWarning' },
						},
					],
					defaultColor: { themeColor: 'statusError' },
				},
			},
			displayStyle: 'text',
		},
		lineBreak('regulator-break-1'),
		{
			id: 'file-summary-label',
			dataConfig: { type: 'custom_text', customText: 'Your file:' },
			styleConfig: muted,
			displayStyle: 'text',
		},
		{
			id: 'file-summary',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'fileSummary', objectTable: TABLE },
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'Regulator vs Your File',
	headers: [{ id: 'h-file-vs-regulator', text: 'Regulator vs Your File' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 180,
};

/** Wrong Number: who the regulator says holds the number on file. */
export const numberBelongsToColumn: ListBuilderColumn = {
	id: 'compliance-number-belongs-to',
	fields: [
		{
			id: 'regulator-holder',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'regulatorHolder', objectTable: TABLE },
				defaultValue: 'Unknown holder',
			},
			styleConfig: { ...plain, weight: 'medium', allowTextWrap: true },
			displayStyle: 'text',
		},
	],
	label: 'Number Belongs To',
	headers: [{ id: 'h-number-belongs-to', text: 'Number Belongs To' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 210,
};

/**
 * Number Not Found (PD-16384): what the lookup found, over when it was checked
 * — "Not found at the NE board" / "Checked on 2 days · last 9/30/2026". Both
 * written by the API from the stored absence record.
 */
export const failedLookupColumn: ListBuilderColumn = {
	id: 'compliance-failed-lookup',
	fields: [
		{
			id: 'failed-lookup-summary',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'failedLookupSummary', objectTable: TABLE },
				defaultValue: 'Not found at the board',
			},
			styleConfig: {
				size: 'sm',
				weight: 'semibold',
				colorRule: { type: 'theme', themeColor: 'statusError' },
				allowTextWrap: true,
			},
			displayStyle: 'text',
		},
		lineBreak('failed-lookup-break-1'),
		{
			id: 'failed-lookup-checked',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'failedLookupChecked', objectTable: TABLE },
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'What We Found',
	headers: [{ id: 'h-failed-lookup', text: 'What We Found' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 204,
};

/**
 * DEA Registered at a Different Address (PD-17113): the reading ("Different
 * address" / "May differ — check"), then where the DEA registers the
 * registration over where the account ships. All three written by the API
 * from the address comparison the dashboard card counts with.
 */
export const deaOtherAddressColumn: ListBuilderColumn = {
	id: 'compliance-dea-other-address',
	fields: [
		{
			id: 'dea-location-label',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'deaLocationLabel', objectTable: TABLE },
			},
			styleConfig: {
				size: 'sm',
				weight: 'semibold',
				colorRule: { type: 'theme', themeColor: 'statusWarning' },
			},
			displayStyle: 'text',
		},
		lineBreak('dea-location-break-1'),
		{
			id: 'dea-registered-at',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'deaRegisteredAt', objectTable: TABLE },
			},
			styleConfig: { ...plain, allowTextWrap: true },
			displayStyle: 'text',
		},
		lineBreak('dea-location-break-2'),
		{
			id: 'dea-ship-to',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'deaShipTo', objectTable: TABLE },
			},
			styleConfig: { ...muted, allowTextWrap: true },
			displayStyle: 'text',
		},
	],
	label: 'Registered vs Ship-To',
	headers: [{ id: 'h-dea-other-address', text: 'Registered vs Ship-To' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 240,
};

/** Controlled, No DEA: what the account bought ("C2, C4 · last 9/4/2026 · 14 invoices"). */
export const controlledPurchasesColumn: ListBuilderColumn = {
	id: 'compliance-controlled-purchases',
	fields: [
		{
			id: 'controlled-purchases',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'controlledPurchases', objectTable: TABLE },
				defaultValue: '-',
			},
			styleConfig: { ...plain, weight: 'medium', allowTextWrap: true },
			displayStyle: 'text',
		},
	],
	label: 'Controlled Purchases',
	headers: [{ id: 'h-controlled-purchases', text: 'Controlled Purchases' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	// The header needs ~190px; a fixed column narrower than its header
	// overflows the table (the list budgets the configured width).
	width: 204,
};

/** Recently Fixed: what the file showed and what it shows now, then when and by whom. */
export const wasNowColumn: ListBuilderColumn = {
	id: 'compliance-was-now',
	fields: [
		{
			id: 'fix-summary',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'fixSummary', objectTable: TABLE },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm',
				weight: 'semibold',
				colorRule: { type: 'theme', themeColor: 'statusSuccess' },
				allowTextWrap: true,
			},
			displayStyle: 'text',
		},
		lineBreak('fix-break-1'),
		{
			id: 'fixed-by',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'fixedBy', objectTable: TABLE },
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'Was → Now',
	headers: [{ id: 'h-was-now', text: 'Was → Now' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 236,
};

/**
 * Adverse Action: WHAT THE BOARD SAYS, and HOW FRESH that reading is.
 *
 * Two lines, both written by the API (licenses.service.ts):
 *   `boardActionSummary` — the action type, case number and dates where the
 *     regulator publishes them; otherwise the explicit sentence that it does
 *     not ("Flag only — no detail on file yet"; PD-16615: what Workd holds,
 *     never a claim about what the board publishes). Most flagged
 *     rows fleet-wide are flag-only, so the ceiling has to be on the row, not
 *     discovered by opening an empty panel.
 *   `boardReadAt` — the date we last read the regulator's own page. A board
 *     action is only as true as the last reading, and a stale one is a
 *     different decision from a fresh one.
 */
export const boardActionColumn: ListBuilderColumn = {
	id: 'compliance-board-action',
	fields: [
		{
			id: 'board-action-summary',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'boardActionSummary', objectTable: TABLE },
				defaultValue: 'Board action on record',
			},
			styleConfig: {
				size: 'sm',
				weight: 'semibold',
				colorRule: { type: 'theme', themeColor: 'statusWarning' },
				allowTextWrap: true,
			},
			displayStyle: 'text',
		},
		lineBreak('board-action-break-1'),
		{
			id: 'board-read-at',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'boardReadAt', objectTable: TABLE },
				defaultValue: 'Board page never read',
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'What the Board Says',
	headers: [{ id: 'h-board-action', text: 'What the Board Says' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	// Sized like the other list-specific columns; the header itself needs
	// ~150px and the flag-only sentence wraps to two or three lines.
	width: 236,
};

/**
 * Adverse Action: the action's OWN dates, start stacked over end.
 *
 * Chris asked for these on the list on 2026-09-20 ("if the list knows the date
 * of the action start/end that would be great to add, and we can just stack
 * those and have dashes when unknown"), and the dashes are most of the column
 * by design: measured on prod the same day, 14 of 276 active-buyer rows yield a
 * start date and 4 yield an end date. A dash here is the BOARD's silence
 * reported, not a gap in our data, and the neighboring "What the Board Says"
 * column already states that ceiling in words.
 *
 * Both lines are written by the API (`boardActionDates` in libs/shared), which
 * is also what the review modal and the auto-sunset predicate read — one
 * reading of the board's dates, so the row, the modal and the queue can never
 * disagree. Each line leads with the BOARD's own frame for its date —
 * "Started" for a discipline start date, "Effective" for an effective date,
 * "Filed" where the board says it filed a charge it has not ruled on, and
 * "Ends"/"Ended" for the end date — so a filed accusation is never presented
 * as a decision the board has made.
 */
export const boardActionDatesColumn: ListBuilderColumn = {
	id: 'compliance-board-action-dates',
	fields: [
		{
			id: 'board-action-started',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'boardActionStarted', objectTable: TABLE },
				defaultValue: '\u2014',
			},
			styleConfig: plain,
			displayStyle: 'text',
		},
		lineBreak('board-action-dates-break-1'),
		{
			id: 'board-action-ended',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'boardActionEnded', objectTable: TABLE },
				defaultValue: '\u2014',
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'Action Dates',
	headers: [{ id: 'h-board-action-dates', text: 'Action Dates' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	// The header needs ~95px and the longest line is "Effective 12/30/1992".
	width: 132,
};

/** One plain sentence, written by the API, saying why the row is on this list. */
export const whyHereColumn: ListBuilderColumn = {
	id: 'compliance-why-here',
	fields: [
		{
			id: 'why-here-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'whyHere', objectTable: TABLE },
			},
			styleConfig: { ...plain, allowTextWrap: true },
			displayStyle: 'text',
		},
	],
	label: "Why It's Here",
	headers: [{ id: 'h-why-here', text: "Why It's Here" }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	minWidth: 260,
};

/**
 * The one next step, as a labelled inline button. Anything that proposes a
 * license opens the matchmaker confirmation screen; nothing is applied from the
 * list (Chris 2026-09-18).
 */
export const actionColumn: ListBuilderColumn = {
	id: 'compliance-action',
	fields: [
		{
			id: 'compliance-actions',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: plain,
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					// PD-16254: the matchmaker found an in-force license at this
					// address. Opens the confirmation screen ON that candidate; the
					// list itself never applies it.
					id: 'review-replacement-action',
					label: 'Review Replacement',
					iconName: 'clipboardCheck',
					callbackName: 'reviewReplacement',
					variant: 'primary',
					visibility: 'visible',
					showLabel: true,
					requiredPermission: 'licenses:update',
					conditionalVisibility: {
						field: 'replacementMatchId',
						condition: 'is_not_null',
					},
				},
				{
					id: 'review-match-action',
					label: 'Review Match',
					iconName: 'clipboardCheck',
					callbackName: 'reviewMatch',
					variant: 'primary',
					visibility: 'visible',
					showLabel: true,
					requiredPermission: 'licenses:update',
					// A pending match and NO replacement to review instead: two
					// fields, so the page supplies the predicate.
					visibilityCallback: 'canReviewMatch',
				},
				{
					id: 'search-registry-action',
					label: 'Search Registry',
					iconName: 'search',
					callbackName: 'searchForMatches',
					variant: 'secondary',
					visibility: 'visible',
					showLabel: true,
					requiredPermission: 'licenses:update',
					// Only a license with NO link and NO pending suggestion: two
					// fields, so the page supplies the predicate.
					visibilityCallback: 'canSearchForMatches',
				},
				{
					// A LINKED license has nothing to review or search: the fix is on
					// the license itself (replace it, or unmatch it).
					id: 'open-license-action',
					label: 'Open License',
					iconName: 'arrowUpRightFromSquare',
					callbackName: 'openLicense',
					variant: 'secondary',
					visibility: 'visible',
					showLabel: true,
					visibilityCallback: 'canOpenLicense',
				},
			],
		},
	],
	label: '',
	headers: [{ id: 'h-compliance-action', text: '' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	alignment: 'right',
	sortable: false,
	// Sized to the longest label, "Review Replacement" (about 178px with its
	// icon) plus the cell padding. At 184 that button overflowed the cell to
	// the left, covering the reason text and clipping its rounded corners.
	width: 216,
};

/**
 * Adverse Action's single action: **Review**, which opens the review modal.
 *
 * Deliberately NOT `actionColumn`: none of that column's four actions applies
 * here. The license is linked and in force, so there is nothing to confirm,
 * search for or replace — the judgement is "should we keep selling on this?",
 * and that is not a decision a list row can carry. Seeing a row means a review
 * is needed; the modal is where the comparison happens (Chris, 2026-09-20).
 */
export const reviewActionColumn: ListBuilderColumn = {
	id: 'compliance-review-action',
	fields: [
		{
			id: 'compliance-review-actions',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: plain,
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'review-adverse-action',
					label: 'Review',
					iconName: 'clipboardCheck',
					callbackName: 'reviewAdverseAction',
					variant: 'primary',
					visibility: 'visible',
					showLabel: true,
					requiredPermission: 'licenses:update',
				},
			],
		},
	],
	label: '',
	headers: [{ id: 'h-compliance-review-action', text: '' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	alignment: 'right',
	sortable: false,
	// One short label, so this column is far narrower than the shared
	// `actionColumn` (which is sized to "Review Replacement"). 116px is the
	// button (~96px with its icon) plus the cell padding.
	width: 116,
};

/**
 * Number Not Found's single action: **Open License**, where the number is
 * corrected (PD-16384). Deliberately NOT `actionColumn`: a number the board
 * does not have cannot be matched, so Review Match and Search Registry have
 * nothing to offer, and that column shows Open License only for a LINKED
 * license — every row here is unlinked.
 */
export const openLicenseActionColumn: ListBuilderColumn = {
	id: 'compliance-open-license-action',
	fields: [
		{
			id: 'compliance-open-license-actions',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: plain,
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'open-license-to-correct',
					label: 'Open License',
					iconName: 'arrowUpRightFromSquare',
					callbackName: 'openLicense',
					variant: 'primary',
					visibility: 'visible',
					showLabel: true,
				},
			],
		},
	],
	label: '',
	headers: [{ id: 'h-compliance-open-license-action', text: '' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	alignment: 'right',
	sortable: false,
	// "Open License" with its icon (~130px) plus the cell padding.
	width: 156,
};

/**
 * PD-16615 — the Flag Only list's two actions: **Look Up**, which opens the
 * board's own page for the record in a new tab, and **Review**, the SAME
 * review the with-detail list offers (same modal, same outcomes). Workd holds
 * nothing beyond the flag yet, so the officer looks the action up at the
 * board and records what they found (Chris, 2026-09-27).
 *
 * Look Up is shown only where we hold a web address for the record
 * (`boardLookupUrl`, written by the API — see `pickBoardLookupUrl`); the row's
 * license number is beside it for a board whose link lands on a search page.
 */
export const flagOnlyActionColumn: ListBuilderColumn = {
	id: 'compliance-flag-only-action',
	fields: [
		{
			id: 'compliance-flag-only-actions',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: plain,
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'open-board-lookup',
					label: 'Look Up',
					iconName: 'arrowUpRightFromSquare',
					callbackName: 'openBoardLookup',
					variant: 'secondary',
					visibility: 'visible',
					showLabel: true,
					conditionalVisibility: {
						field: 'boardLookupUrl',
						condition: 'is_not_null',
					},
				},
				{
					id: 'review-flag-only-action',
					label: 'Review',
					iconName: 'clipboardCheck',
					callbackName: 'reviewAdverseAction',
					variant: 'primary',
					visibility: 'visible',
					showLabel: true,
					requiredPermission: 'licenses:update',
				},
			],
		},
	],
	label: '',
	headers: [{ id: 'h-compliance-flag-only-action', text: '' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	alignment: 'right',
	sortable: false,
	// Two short labelled buttons side by side: "Look Up" (~104px with its
	// icon) and "Review" (~96px), plus the gap and the cell padding.
	width: 232,
};

/**
 * PD-16615 — the Reviewed list: the decision that is hiding the row, over when
 * it comes back. `reviewOutcome` reads "Reviewed — no concern", "Reviewed —
 * action taken" or "Follow up on 10/15/2026"; `reviewFollowUp` says when a
 * follow-up returns ("Back in 18 days") and is empty for the other two.
 */
export const reviewOutcomeColumn: ListBuilderColumn = {
	id: 'compliance-review-outcome',
	fields: [
		{
			id: 'review-outcome',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'reviewOutcome', objectTable: TABLE },
				defaultValue: '—',
			},
			styleConfig: { ...plain, weight: 'semibold', allowTextWrap: true },
			displayStyle: 'text',
		},
		lineBreak('review-outcome-break-1'),
		{
			id: 'review-follow-up',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'reviewFollowUp', objectTable: TABLE },
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'statusWarning' },
			},
			displayStyle: 'text',
		},
	],
	label: 'Decision',
	headers: [{ id: 'h-review-outcome', text: 'Decision' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	// The longest line is "Reviewed — action taken".
	width: 200,
};

/**
 * PD-16615 — the Reviewed list: the officer's mandatory note, over who
 * recorded the decision and when ("Casey Officer · 9/27/2026").
 */
export const reviewNoteColumn: ListBuilderColumn = {
	id: 'compliance-review-note',
	fields: [
		{
			id: 'review-note',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'reviewNote', objectTable: TABLE },
				defaultValue: '—',
			},
			styleConfig: { ...plain, allowTextWrap: true },
			displayStyle: 'text',
		},
		lineBreak('review-note-break-1'),
		{
			id: 'reviewed-by',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'reviewedBy', objectTable: TABLE },
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'Note',
	headers: [{ id: 'h-review-note', text: 'Note' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	minWidth: 260,
};

/**
 * PD-16615 — the Reviewed list's single action: **Open**, the review modal,
 * which shows the decision in full with its history and offers Re-open (the
 * undo) or a new decision. Deliberately not a one-click undo on the row: the
 * officer sees what they are undoing first.
 */
export const reviewedActionColumn: ListBuilderColumn = {
	id: 'compliance-reviewed-action',
	fields: [
		{
			id: 'compliance-reviewed-actions',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: plain,
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'open-adverse-review',
					label: 'Open',
					iconName: 'clipboardCheck',
					callbackName: 'reviewAdverseAction',
					variant: 'secondary',
					visibility: 'visible',
					showLabel: true,
					requiredPermission: 'licenses:update',
				},
			],
		},
	],
	label: '',
	headers: [{ id: 'h-compliance-reviewed-action', text: '' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	alignment: 'right',
	sortable: false,
	width: 116,
};

// ── Check Your Records: Duplicate License / Shared License (PD-16652) ───────
//
// One row per license in a GROUP (the same license twice on one account, or
// one license on several accounts), the group's rows together, riskiest group
// first. Every field below is written by the API
// (`LicenseDuplicateGroupService`, `duplicateGroupListView`); none of these
// columns sorts, because a sort by any one of them would scatter the groups.

/**
 * A group's first row (PD-16654): the group heads the block of its rows.
 * `duplicateGroupPosition` is the row's 1-based place inside its group.
 */
const groupFirstRow = {
	field: 'duplicateGroupPosition',
	condition: 'equals' as const,
	value: 1,
};

/**
 * The group: "DEA BE7893921 · 3 accounts" over why it ranks where it does, on
 * the group's FIRST row. Its later rows repeat only the label, small and
 * muted (PD-16654), so the eye reads one block per group — the list also
 * shades alternate groups and rules a line above each — while a group that
 * carries over onto the next page still names itself at the top.
 */
export const duplicateGroupColumn: ListBuilderColumn = {
	id: 'compliance-duplicate-group',
	fields: [
		{
			id: 'duplicate-group-label',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'duplicateGroupLabel', objectTable: TABLE },
			},
			conditionalVisibility: groupFirstRow,
			styleConfig: { ...plain, weight: 'semibold', allowTextWrap: true },
			displayStyle: 'text',
		},
		lineBreak('duplicate-group-break-1'),
		{
			id: 'duplicate-group-reason',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'duplicateGroupReasonLabel', objectTable: TABLE },
			},
			conditionalVisibility: groupFirstRow,
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'statusWarning' },
			},
			displayStyle: 'text',
		},
		lineBreak('duplicate-group-break-2'),
		{
			id: 'duplicate-group-continued',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'duplicateGroupLabel', objectTable: TABLE },
			},
			conditionalVisibility: { ...groupFirstRow, condition: 'not_equals' },
			styleConfig: { ...muted, allowTextWrap: true },
			displayStyle: 'text',
		},
	],
	label: 'Group',
	headers: [{ id: 'h-duplicate-group', text: 'Group' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 200,
};

/**
 * The account: name (links to the client), then "ERP 1367668 · Open · Last
 * order 9/3/2026", then its parent account when it has one.
 */
export const duplicateAccountColumn: ListBuilderColumn = {
	id: 'compliance-duplicate-account',
	fields: [
		{
			id: 'duplicate-account-name',
			link: {
				type: 'custom',
				enabled: true,
				urlTemplate: '/clients/{clientId}',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientName', objectTable: TABLE },
			},
			// Wrapped, never cut: two accounts in one group often differ only
			// at the end of the name ("… #2", "… - CLOSED") — PD-16654.
			styleConfig: {
				size: 'sm',
				weight: 'medium',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
				allowTextWrap: true,
			},
			displayStyle: 'text',
		},
		lineBreak('duplicate-account-break-1'),
		{
			id: 'duplicate-account-summary',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'accountSummary', objectTable: TABLE },
			},
			styleConfig: {
				size: 'xs',
				// A closed account is the finding on half the Shared License
				// reasons, so it reads in the warning color.
				colorRule: {
					type: 'conditional',
					rules: [
						{
							field: { fieldName: 'accountStatusLabel', objectTable: TABLE },
							operator: 'equals',
							value: 'Closed',
							color: { themeColor: 'statusWarning' },
						},
					],
					defaultColor: { themeColor: 'textSecondary' },
				},
				allowTextWrap: true,
			},
			displayStyle: 'text',
		},
		lineBreak('duplicate-account-break-2'),
		{
			id: 'duplicate-account-parent',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'parentAccountName', objectTable: TABLE },
			},
			prefix: 'Parent: ',
			conditionalVisibility: {
				field: 'parentAccountName',
				condition: 'is_not_null',
			},
			styleConfig: { ...muted, allowTextWrap: true },
			displayStyle: 'text',
		},
	],
	label: 'Account',
	headers: [{ id: 'h-duplicate-account', text: 'Account' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	// "ERP F90945886 · Open · Last order 9/3/2026" on one line where the
	// table has room; it wraps below this rather than being cut.
	minWidth: 260,
};

/** The ship-to address the copy hangs on. */
export const shipToColumn: ListBuilderColumn = {
	id: 'compliance-ship-to',
	fields: [
		{
			id: 'ship-to-street',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientStreetAddress', objectTable: TABLE },
				defaultValue: '-',
			},
			styleConfig: { ...plain, allowTextWrap: true },
			displayStyle: 'text',
		},
		lineBreak('ship-to-break-1'),
		{
			id: 'ship-to-city',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientCityStateZip', objectTable: TABLE },
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'Ship-To',
	headers: [{ id: 'h-ship-to', text: 'Ship-To' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 200,
};

/** The copy: its number AS STORED (links to the license), type and ERP id. */
export const duplicateLicenseColumn: ListBuilderColumn = {
	id: 'compliance-duplicate-license',
	fields: [
		{
			id: 'duplicate-license-number',
			link: { type: 'custom', enabled: true, urlTemplate: '/licenses/{id}' },
			dataConfig: {
				type: 'field',
				field: { fieldName: 'licenseNumberLabel', objectTable: TABLE },
			},
			styleConfig: {
				size: 'sm',
				weight: 'medium',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
			},
			displayStyle: 'text',
		},
		lineBreak('duplicate-license-break-1'),
		{
			id: 'duplicate-license-type',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'type', objectTable: TABLE },
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
		lineBreak('duplicate-license-break-2'),
		{
			id: 'duplicate-license-erp-id',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'licenseErpId', objectTable: TABLE },
			},
			prefix: 'ERP id: ',
			conditionalVisibility: {
				field: 'licenseErpId',
				condition: 'is_not_null',
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'License',
	headers: [{ id: 'h-duplicate-license', text: 'License' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 190,
};

/**
 * What this copy says: "Active to 12/31/2026", then the number's format hint
 * ("Contains hidden characters · Formats differ: PH26363, 26363") and whether
 * it is confirmed to the same board record as another member.
 */
export const duplicateOnFileColumn: ListBuilderColumn = {
	id: 'compliance-duplicate-on-file',
	fields: [
		{
			id: 'duplicate-file-summary',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'licenseFileSummary', objectTable: TABLE },
			},
			styleConfig: { ...plain, weight: 'medium' },
			displayStyle: 'text',
		},
		lineBreak('duplicate-on-file-break-1'),
		{
			id: 'duplicate-number-format-hint',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'numberFormatHint', objectTable: TABLE },
			},
			conditionalVisibility: {
				field: 'numberFormatHint',
				condition: 'is_not_null',
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'theme', themeColor: 'statusWarning' },
				allowTextWrap: true,
			},
			displayStyle: 'text',
		},
		lineBreak('duplicate-on-file-break-2'),
		{
			id: 'duplicate-board-record',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'boardRecordSummary', objectTable: TABLE },
			},
			conditionalVisibility: {
				field: 'boardRecordSummary',
				condition: 'is_not_null',
			},
			styleConfig: { ...muted, allowTextWrap: true },
			displayStyle: 'text',
		},
	],
	label: 'On File',
	headers: [{ id: 'h-duplicate-on-file', text: 'On File' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 220,
};

/**
 * The one action: **Review Group**, which opens the group review (every member
 * side by side, the "Fine as is" / "Being fixed" decision and its note;
 * PD-16653 / PD-16654). A row stands for its whole group, so the action is
 * the same on each of the group's rows.
 */
export const reviewGroupActionColumn: ListBuilderColumn = {
	id: 'compliance-review-group-action',
	fields: [
		{
			id: 'compliance-review-group-actions',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: plain,
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'review-duplicate-group',
					label: 'Review Group',
					iconName: 'clipboardCheck',
					callbackName: 'reviewDuplicateGroup',
					variant: 'primary',
					visibility: 'visible',
					showLabel: true,
					requiredPermission: 'licenses:update',
				},
			],
		},
	],
	label: '',
	headers: [{ id: 'h-compliance-review-group-action', text: '' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	alignment: 'right',
	sortable: false,
	// "Review Group" with its icon (~140px) plus the cell padding.
	width: 164,
};

/**
 * PD-16653 — the Check Your Records Reviewed lists' single action: **Open**,
 * the group review, which shows the decision with its history and offers
 * Reopen (the undo) or a new decision. Not a one-click undo on the row: the
 * reviewer sees what they are undoing first, as on Adverse Action.
 */
export const reviewedGroupActionColumn: ListBuilderColumn = {
	id: 'compliance-reviewed-group-action',
	fields: [
		{
			id: 'compliance-reviewed-group-actions',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: plain,
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'open-duplicate-group-review',
					label: 'Open',
					iconName: 'clipboardCheck',
					callbackName: 'reviewDuplicateGroup',
					variant: 'secondary',
					visibility: 'visible',
					showLabel: true,
					requiredPermission: 'licenses:update',
				},
			],
		},
	],
	label: '',
	headers: [{ id: 'h-compliance-reviewed-group-action', text: '' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	alignment: 'right',
	sortable: false,
	width: 116,
};
